import { RECALL_GOAL_LABEL } from '../settings-recall-agent/types';
import type { RecallGoal } from '../settings-recall-agent/types';
import type { RcListRow, RcState } from './types';

/**
 * **One list, read by four surfaces** (amendment 22 §8).
 *
 * The complaint was that `/recall`, AI Team › Recall and the Recall settings link
 * strip each showed a different set of recalls and a different count. They now all
 * read this store, so they cannot disagree — and the toggle, Duplicate and Delete
 * write to it, which is why it is mutable rather than a frozen fixture.
 */

/** §7 — the seed list for staging. Nine recalls, six of them running. */
const SEED: RcListRow[] = [
  {
    id: 'hygiene-6',
    name: 'Hygiene — 6 month',
    state: 'active',
    goal: 'book_appointment',
    since: '3 Mar',
    inProgress: 412,
    steps: ['text', 'email', 'ai_call', 'human_call'],
    last30: 58,
  },
  {
    id: 'perio-3',
    name: 'Perio maintenance — 3 month',
    state: 'active',
    goal: 'book_appointment',
    since: '3 Mar',
    inProgress: 88,
    steps: ['text', 'email', 'ai_call'],
    last30: 14,
  },
  {
    id: 'annual-exam',
    name: 'Annual exam',
    state: 'active',
    goal: 'book_appointment',
    since: '3 Mar',
    inProgress: 214,
    steps: ['text', 'email', 'ai_call'],
    last30: 22,
  },
  {
    id: 'oral-cancer',
    name: 'Oral cancer screening',
    state: 'active',
    goal: 'book_appointment',
    since: '14 Apr',
    inProgress: 96,
    steps: ['text', 'email'],
    last30: 9,
  },
  {
    id: 'lapsed-18',
    name: 'Lapsed — 18 months and over',
    state: 'active',
    goal: 'reactivate_patient',
    since: '3 Mar',
    inProgress: 1316,
    steps: ['text', 'email', 'ai_call', 'human_call'],
    last30: 21,
  },
  {
    // Loyalty carries no ask, so its one number is `sent` rather than `booked`.
    id: 'birthday',
    name: 'Birthday',
    state: 'active',
    goal: 'increase_loyalty',
    since: '3 Mar',
    inProgress: 3,
    steps: ['text'],
    last30: 61,
  },
  {
    id: 'fluoride',
    name: 'Fluoride — under 14',
    state: 'paused',
    goal: 'book_appointment',
    since: '12 Jan',
    pausedOn: '14 Sep',
    inProgress: 42,
    steps: ['text', 'email'],
    last30: 3,
  },
  {
    id: 'whitening',
    name: 'Whitening — summer',
    state: 'paused',
    goal: 'book_appointment',
    since: '1 Jun',
    pausedOn: '31 Aug',
    inProgress: 57,
    steps: ['text', 'email', 'email'],
    last30: 4,
  },
  {
    // A draft has no patients, no steps and no result — three em dashes, not zeros.
    id: 'implant-check',
    name: 'Implant check — annual',
    state: 'draft',
    goal: 'book_appointment',
    steps: [],
  },
];

export const recallStore: { rows: RcListRow[] } = { rows: SEED.map((r) => ({ ...r })) };

/**
 * Distinct patients. **Not the column added up** — somebody due for hygiene and
 * overdue for perio is one patient in two recalls, so the header figure is lower
 * than the sum and §9 asserts exactly that.
 */
export const PATIENTS_IN_PROGRESS = 2131;

/** The Dashboard's 30-day Recall figure. One query, one number (§8). */
export const BOOKED_LAST_30 = 131;

export function activeRows(): RcListRow[] {
  return recallStore.rows.filter((r) => r.state === 'active');
}

/** Active, then paused, then draft; within each, most patients first (§3). */
const ORDER: Record<RcState, number> = { active: 0, paused: 1, draft: 2 };

export function sortedRows(): RcListRow[] {
  return [...recallStore.rows].sort(
    (a, b) => ORDER[a.state] - ORDER[b.state] || (b.inProgress ?? 0) - (a.inProgress ?? 0),
  );
}

export function recallById(id: string): RcListRow | undefined {
  return recallStore.rows.find((r) => r.id === id);
}

/** Active ⇄ Paused, immediately and with no dialog (§4). */
export function toggleRecall(id: string): RcListRow | undefined {
  const row = recallById(id);
  if (row === undefined || row.state === 'draft') return undefined;
  if (row.state === 'active') {
    row.state = 'paused';
    row.pausedOn = todayLabel();
  } else {
    row.state = 'active';
    delete row.pausedOn;
  }
  return row;
}

/**
 * A copy carries the shape and none of the history — **no patients, no results**,
 * because a duplicate has not run and a number saying otherwise would be a lie.
 */
export function duplicateRecall(id: string): RcListRow | undefined {
  const row = recallById(id);
  if (row === undefined) return undefined;
  const copy: RcListRow = {
    id: `${row.id}-copy-${String(Date.now())}`,
    name: `Copy of ${row.name}`,
    state: 'draft',
    goal: row.goal,
    steps: [...row.steps],
  };
  recallStore.rows = [...recallStore.rows, copy];
  return copy;
}

export function deleteRecall(id: string): void {
  recallStore.rows = recallStore.rows.filter((r) => r.id !== id);
}

/** The header, computed — never written down (§2). */
export function listHeader(): { recalls: number; active: number; inProgress: number; booked: number } {
  return {
    recalls: recallStore.rows.length,
    active: activeRows().length,
    inProgress: PATIENTS_IN_PROGRESS,
    booked: BOOKED_LAST_30,
  };
}

/** What the one number is called, per goal (§3). */
export function resultNoun(goal: RecallGoal): string {
  if (goal === 'increase_loyalty') return 'sent';
  if (goal === 'clinical_safety') return 'replied';
  return 'booked';
}

/**
 * `Book` · `Reactivate` · `Increase loyalty` · `Clinical safety` (§3).
 *
 * Re-exported from the one map rather than written again — a second copy is how
 * `Loyalty` and `Increase loyalty` ended up on two different screens.
 */
export const LIST_GOAL_LABEL = RECALL_GOAL_LABEL;

function todayLabel(): string {
  const d = new Date();
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getDate())} ${months[d.getMonth()] ?? ''}`;
}
